import { Avatar, Box, CircularProgress, Tooltip, Typography } from '@mui/material'
import React, { FC, ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

type Size = 'xs' | 'sm' | 'md' | 'lg'

const SIZE_PX: Record<Size, number> = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 48,
}

interface EntityAvatarProps {
  name?: string | null
  photoUrl?: string
  size?: Size
  subtitle?: string
  fallbackIcon?: ReactNode

  // display controls
  showImage?: boolean
  showName?: boolean

  // tooltip - when provided, wraps the avatar in a Tooltip
  tooltip?: string | boolean // true uses name as tooltip, string for custom tooltip

  // navigation - when enabled, clicking navigates to /profile/:userId
  userId?: string
  enableNav?: boolean

  // behavior / styling
  isLoading?: boolean
  className?: string
  style?: React.CSSProperties
}

const EntityAvatar: FC<EntityAvatarProps> = ({
  name,
  photoUrl,
  size = 'sm',
  subtitle,
  fallbackIcon,
  showImage = true,
  showName = false,
  tooltip,
  userId,
  enableNav = false,
  isLoading = false,
  className,
  style,
}) => {
  const navigate = useNavigate()
  const px = SIZE_PX[size]

  const safeName = name ?? ''

  const initials = useMemo(() => {
    const parts = safeName.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return '?'
    const first = parts[0]?.[0] ?? ''
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''
    return (first + last).toUpperCase()
  }, [safeName])

  const isClickable = enableNav && !!userId

  const handleClick = (e: React.MouseEvent) => {
    if (isClickable) {
      e.stopPropagation()
      navigate(`/teams/profile/${userId}`)
    }
  }

  // Determine tooltip text: true uses name, string uses custom text
  const tooltipText = tooltip === true ? safeName : typeof tooltip === 'string' ? tooltip : ''

  // Render avatar with hover effect (matching EntityAvatarGroup pattern)
  const avatar = (
    <Avatar
      src={photoUrl}
      alt={safeName}
      onClick={isClickable ? handleClick : undefined}
      sx={{
        width: px,
        height: px,
        fontSize: Math.max(10, Math.floor(px / 2.4)),
        fontWeight: 600,
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        backgroundColor: style?.backgroundColor ?? '',
        opacity: style?.opacity ?? 1,
        '&:hover': {
          transform: 'scale(1.1)',
          zIndex: 10,
          boxShadow: 3,
        },
      }}
    >
      {fallbackIcon || initials}
    </Avatar>
  )

  // If only showing image (no name), render just the avatar
  if (showImage && !showName) {
    if (isLoading) {
      return <CircularProgress size={px} thickness={4} />
    }

    // Wrap in tooltip if needed
    if (tooltip && tooltipText) {
      return (
        <Tooltip title={tooltipText} arrow>
          {avatar}
        </Tooltip>
      )
    }

    return avatar
  }

  // If showing name, render avatar + name in a flex container
  const content = (
    <Box
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        ...style,
      }}
    >
      {showImage && (
        <>
          {isLoading ? (
            <CircularProgress size={px} thickness={4} />
          ) : tooltip && tooltipText ? (
            <Tooltip title={tooltipText} arrow>
              {avatar}
            </Tooltip>
          ) : (
            avatar
          )}
        </>
      )}

      {showName && (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={safeName}
          >
            {safeName}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={subtitle}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )

  return content
}

export default EntityAvatar
